import React, {useEffect, useState} from "react";
import {DefaultContainer} from "@shared/DefaultContainer.tsx";
import {IRecursoMovimentacao} from "@models/recurso/IRecursoMovimentacao.ts";
import {_RecursosEndpoints} from "@localization/siteMap/endpoints/_RecursosEndpoints.ts";
import {IObjectCounter, IPagination} from "@localization/siteMap/siteMapInterfaces.ts";
import {
    Button,
    Grid,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from "@mui/material";
import {BarChart} from '@mui/x-charts/BarChart';
import {MoreHoriz, SearchOutlined} from "@mui/icons-material";
import {useAlert} from "@contexts/hooks/useAlert.ts";
import {DatePicker} from "@mui/x-date-pickers";
import dayjs from "dayjs";
import {
    RecursoMovimentacaoInfoGrid,
    RecursoMovimentacaoInfoRow
} from "@components/recurso/movimentacao/RecursoMovimentacaoInformacao.tsx";
import {useGeneral} from "@contexts/GeneralContext";
import {RecursosMovimentacoesFilters} from "@models/recurso/movimentacoes/RecursosMovimentacoesFilters.ts";
import {DefaultEventProps} from "@models/DefaultProps.ts";
import {useApi} from "@contexts/hooks/api/useApi.ts";

export interface RecursoMovimentacoesPageProps {
    filters?: RecursosMovimentacoesFilters,
    onCreate?: (movimentacao: IRecursoMovimentacao) => void;
}

type visualizacao = 'Tabela' | 'Grafico'

export function RecursosMovimentacoesV2Page(props: RecursoMovimentacoesPageProps & Pick<DefaultEventProps, 'onUpdate'>) {
    const {Get} = useApi();
    const {addPopUp} = useAlert();
    const {screenSizeLevel} = useGeneral();
    const [refreshRequested, setRefreshRequested] = useState(false);

    const [visualizacao, _setVisualizacao] = useState<visualizacao>('Tabela');
    const [filters, setFilters] = useState<RecursosMovimentacoesFilters>({
        ...props.filters,
        dataInicial: dayjs(props.filters?.dataInicial ?? '2000-01-01'),
        dataFinal: dayjs(props.filters?.dataFinal ?? undefined),
    });

    const [movimentacoes, setMovimentacoes] = useState<IRecursoMovimentacao[]>();
    const [counters, _setCounters] = useState<IObjectCounter[]>([]);
    const [pagination, setPagination] = useState<IPagination>({
            page: 0, pageSize: 10,
        },
    );

    useEffect(() => {
        if (pagination.page == 0) {
            setPagination({...pagination, page: 1});
            return;
        }
        setPagination(prev => ({...prev, loading: true}));
        Get(_RecursosEndpoints.GetRecursosMovimentacoes({...filters}, pagination), refreshRequested).then(x => {
            if (x.status == 'Success') {
                setMovimentacoes(x.items);
                setRefreshRequested(false)
            }
            setPagination(prev => ({...prev, ...x.pagination}));

        }).finally(() => {
            setPagination(prev => ({...prev, loading: false, ignoreCache: false}));

        });
    }, [pagination.page, pagination.ignoreCache == true]);

    return (<DefaultContainer pageProps={{pagination, setPagination}} UpperComponent={<Grid container justifyContent={'space-between'} sx={{mb: 1, mt: 1}}>
	    <Grid container>
		    <Grid size={screenSizeLevel > 6 ? 12 : 6}>
			    <DatePicker
				    label={'Data inicial'}
				    value={filters.dataInicial}
				    maxDate={filters.dataFinal}
				    minDate={dayjs('2000-01-01')}
				    onChange={(newValue) => {
					    setFilters({
						    ...filters,
						    dataInicial: dayjs(newValue),
					    });
				    }}
				    localeText={{
					    'todayButtonLabel': 'Hoje',
				    }}
				    slotProps={{
					    textField: {size: 'small', variant: 'filled', fullWidth: true}, actionBar: {
						    actions: ['today'],
					    },
				    }}
			    />
		    </Grid>
		    <Grid size={screenSizeLevel > 6 ? 12 : 6}>
			    <DatePicker
				    label={'Data final'}
				    value={filters.dataFinal}
				    minDate={filters.dataInicial}
				    maxDate={dayjs().add(5000, 'year')}
				    localeText={{
					    'todayButtonLabel': 'Hoje',
				    }}
				    slotProps={{
					    textField: {size: 'small', variant: 'filled', fullWidth: true}, actionBar: {
						    actions: ['today'],
					    },
				    }}
				    onChange={(newValue) => {
					    setFilters({
						    ...filters,
						    dataFinal: dayjs(newValue),
					    });
				    }}
			    />
		    </Grid>
	    </Grid>
	    <Grid>
		    <Button fullWidth
		            loading={pagination.loading ?? false}
		            variant={'outlined'}
		            sx={{maxHeight: '100%', height: '100%'}} onClick={() => {
			    setPagination({...pagination, page: 0});
		    }}
		            endIcon={<SearchOutlined/>}
		    >Filtrar</Button>
	    </Grid>
    </Grid>}
                              permissaoNecessaria={['VisualizarRecursoInformacoes']}>
        {visualizacao == 'Tabela' && <Grid>
            <TableContainer>
                <Table size={'small'} stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell>Id</TableCell>
                            <TableCell>Efetuado</TableCell>
                            <TableCell>Pendente</TableCell>
                            <TableCell>Cancelado</TableCell>
                            <TableCell>Motivo</TableCell>
                            <TableCell>Data</TableCell>
                            <TableCell>Tipo</TableCell>
                            <TableCell>Mais</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {movimentacoes?.map(x => {
                            return (<RecursoMovimentacaoInfoRow
                                value={x}
                                EndComponent={() => (
                                    <IconButton onClick={() => {
                                        addPopUp({
                                            title: 'Movimentação ' + x.id,
                                            content: <RecursoMovimentacaoInfoGrid value={x} onUpdate={(x) => {
                                                setRefreshRequested(true)
                                                if (props.onUpdate != null)
                                                    props.onUpdate(x)
                                            }}/>,
                                            size: 'lg'
                                        })
                                    }}>
                                        <MoreHoriz/>
                                    </IconButton>
                                )}/>)
                        })}
                    </TableBody>
                </Table>

            </TableContainer>

        </Grid>

        }
        {visualizacao == 'Grafico' && <Grid container>
            <Grid size={12}>
                <BarChart height={300}
                          xAxis={[{data: counters.map(x => x.name)}]}
                          series={[{data: counters.map(x => Math.abs(x.value ?? 0))}]}

                />
            </Grid>
        </Grid>
        }
    </DefaultContainer>);
}
